import { Device } from '../../../services/Device';
import { getSessionDeviceDisplayName } from '../../../constants/deviceTypeNames';
import { mqttAmplifierTopicIdFromSessionDevice } from '../../../utils/amplifierBackendResponse';
import type { RadioRemoteSessionDevice } from './types';
import type { RadioRemoteLocaleKey } from '../../../i18n/locales/radioRemote';

const DEVICE_PAGE_SNAPSHOT_STORAGE_KEY = 'wavelink.devicePageSnapshot';

export function sessionDeviceStatusKey(status?: string): RadioRemoteLocaleKey | null {
  if (status === 'online') return 'status.online';
  if (status === 'in_use') return 'status.inUse';
  if (status === 'offline') return 'status.offline';
  if (status) return null;
  return 'status.unknown';
}

export function isSessionDeviceOnline(device: RadioRemoteSessionDevice): boolean {
  return device.status === 'online' || device.status === 'in_use';
}

export function countSessionDeviceOnlineCount(devices: RadioRemoteSessionDevice[]): number {
  return devices.filter(isSessionDeviceOnline).length;
}

/** 会话设备 SN：优先 serialNumber，兼容 serial_number；仅接受有限十进制数 */
export function getSessionDeviceSerialNumber(device: RadioRemoteSessionDevice): number | null {
  const raw: unknown = device.serialNumber ?? device.serial_number;
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return raw;
  }
  if (typeof raw === 'string' && /^\d+$/.test(raw.trim())) {
    return Number(raw.trim());
  }
  return null;
}

/** 设备详情深链：/{deviceTypeId}/{SN} */
export function buildSessionDevicePagePath(device: RadioRemoteSessionDevice): string | null {
  const typeId = device.deviceTypeId;
  const sn = getSessionDeviceSerialNumber(device);
  if (typeId == null || !Number.isFinite(typeId) || sn == null) {
    return null;
  }
  return `/${Number(typeId)}/${sn}`;
}

export function parseDevicePageRouteParams(
  deviceTypeId: string | undefined,
  serialNumber: string | undefined
): { deviceTypeId: number; serialNumber: number } | null {
  if (!deviceTypeId || !serialNumber) {
    return null;
  }
  if (!/^\d+$/.test(deviceTypeId) || !/^\d+$/.test(serialNumber)) {
    return null;
  }
  return { deviceTypeId: Number(deviceTypeId), serialNumber: Number(serialNumber) };
}

export function matchSessionDeviceByTypeAndSn(
  devices: readonly RadioRemoteSessionDevice[],
  deviceTypeId: number,
  serialNumber: number
): RadioRemoteSessionDevice | undefined {
  return devices.find((device) => {
    if (device.deviceTypeId !== deviceTypeId) {
      return false;
    }
    return getSessionDeviceSerialNumber(device) === serialNumber;
  });
}

export function writeSessionDevicePageSnapshot(device: RadioRemoteSessionDevice): void {
  const typeId = device.deviceTypeId;
  const sn = getSessionDeviceSerialNumber(device);
  if (typeId == null || sn == null || typeof sessionStorage === 'undefined') {
    return;
  }
  try {
    sessionStorage.setItem(
      DEVICE_PAGE_SNAPSHOT_STORAGE_KEY,
      JSON.stringify({
        deviceTypeId: Number(typeId),
        serialNumber: sn,
        device,
      })
    );
  } catch {
    /* ignore quota / private mode */
  }
}

export function readSessionDevicePageSnapshot(
  deviceTypeId: number,
  serialNumber: number
): RadioRemoteSessionDevice | null {
  if (typeof sessionStorage === 'undefined') {
    return null;
  }
  try {
    const raw = sessionStorage.getItem(DEVICE_PAGE_SNAPSHOT_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as {
      deviceTypeId?: number;
      serialNumber?: number;
      device?: RadioRemoteSessionDevice;
    };
    if (parsed.deviceTypeId !== deviceTypeId || parsed.serialNumber !== serialNumber) {
      return null;
    }
    if (!parsed.device || typeof parsed.device !== 'object') {
      return null;
    }
    return parsed.device;
  } catch {
    return null;
  }
}

export type DevicePageShellInit = {
  overlayOpen: boolean;
  detailPanelMode: 'radio' | 'amplifier';
  amplifierOverlayDeviceId: string | null;
  amplifierOverlayRemoteControlDeviceId: number | null;
  amplifierOverlaySerialLabel: string;
  spectrumDeviceId: number | null;
  overlayBoundSessionDeviceId: number | null;
  detailTitle: string;
};

/** 深链首屏：立刻铺详情壳，数据稍后由会话/MQTT 填入 */
export function getDevicePageShellInit(
  deviceTypeId?: string,
  serialNumber?: string
): DevicePageShellInit {
  const empty: DevicePageShellInit = {
    overlayOpen: false,
    detailPanelMode: 'radio',
    amplifierOverlayDeviceId: null,
    amplifierOverlayRemoteControlDeviceId: null,
    amplifierOverlaySerialLabel: '',
    spectrumDeviceId: null,
    overlayBoundSessionDeviceId: null,
    detailTitle: 'BladeRF xA9',
  };
  const target = parseDevicePageRouteParams(deviceTypeId, serialNumber);
  if (!target) {
    return empty;
  }
  const snap = readSessionDevicePageSnapshot(target.deviceTypeId, target.serialNumber);
  const device: RadioRemoteSessionDevice = snap ?? {
    deviceTypeId: target.deviceTypeId,
    serialNumber: target.serialNumber,
  };
  const sid = device.id ?? device.deviceId;
  const boundId = sid != null && Number.isFinite(Number(sid)) ? Number(sid) : null;
  if (target.deviceTypeId === 9) {
    return {
      overlayOpen: true,
      detailPanelMode: 'amplifier',
      amplifierOverlayDeviceId: mqttAmplifierTopicIdFromSessionDevice(device) ?? '',
      amplifierOverlayRemoteControlDeviceId: getSessionDeviceSerialNumber(device),
      amplifierOverlaySerialLabel: `SN ${target.serialNumber}`,
      spectrumDeviceId: null,
      overlayBoundSessionDeviceId: boundId,
      detailTitle: getSessionDeviceDisplayName(device),
    };
  }
  return {
    overlayOpen: true,
    detailPanelMode: 'radio',
    amplifierOverlayDeviceId: null,
    amplifierOverlayRemoteControlDeviceId: null,
    amplifierOverlaySerialLabel: '',
    spectrumDeviceId: boundId,
    overlayBoundSessionDeviceId: boundId,
    detailTitle: getSessionDeviceDisplayName(device),
  };
}

/** 供卡片 PttControl 与详情页一致：由会话设备构造 Device */
export function buildPttDeviceForSession(
  device: RadioRemoteSessionDevice,
  idNum: number
): Device | null {
  if (!Number.isFinite(idNum) || idNum < 1) return null;
  const deviceInfo = {
    id: String(idNum),
    ipAddress: device.localIp || '',
    tcpPort: device.tcpPort ?? 8080,
    udpPort: device.udpPort ?? 8081,
    name: getSessionDeviceDisplayName(device),
  };
  const o = new Device(deviceInfo);
  if (isSessionDeviceOnline(device)) {
    o.updateConnectionStatus(true);
  }
  return o;
}
